// Schema-aware table for `events-v1` registry files (Timeline Lane
// 1.0.0). Renders registry/events.yaml — 242 deep-time historical
// events (BCE → modern) consumed at runtime by the standalone D3
// timeline viz at code-tron/timeline-v010-precise/. Read-only — the
// write path is the git round-trip, not this UI, and the file's
// events[] shape must NOT be reshaped (the sibling viz iterates it).
//
// Columns: Name (bold), Date (BCE-aware, sortable chronological —
// numeric year, negative < positive), Span (start – end when endDate
// present, em-dash otherwise), Track (verbose label + color chip keyed
// off the `cat` short code), Location, Description (truncated + full
// on hover), Source (linked sourceUrl + sourceLabel text).

import { useMemo, useState } from 'react';
import yaml from 'js-yaml';
import { ExternalLink } from 'lucide-react';
import { validateRegistryEntry } from '@captain-tasko/shared';
import { WarningRow } from '../WarningRow.js';
import { RegistryTableHeader } from '../RegistryTableHeader.js';
import { SortableTh } from '../SortableTh.js';
import { useTableSort } from '../../lib/sort.js';
import { formatBceDate, formatBceSpan, parseYear, coerceDateString } from './formatDate.js';

// ── Schema, mirrors registry/events.yaml ────────────────────────────
interface DeepTimeEvent {
  name: string;
  /** ISO 8601; negative years for BCE. js-yaml may hand back a string
   *  or (for CE dates) a Date — coerced before use. */
  date: string;
  endDate?: string;
  /** Thematic category, verbose (e.g. "Dynasties & States"). */
  track: string;
  /** Short code (empire/eng/pol/culture/war/…). Drives the chip color. */
  cat?: string;
  location?: string;
  desc?: string;
  sourceLabel?: string;
  sourceUrl?: string;
}

interface EventsFile {
  schema_version?: number;
  events?: DeepTimeEvent[];
  [k: string]: unknown;
}

// Chip colors keyed off the `cat` short code. Deep-time vocabulary is
// open-ended — unknown codes fall back to the neutral tone.
const CAT_COLORS: Record<string, string> = {
  empire: '#7A4D8E',
  eng: '#3C8E7A',
  pol: '#2A5C8C',
  culture: '#A56B00',
  war: '#B3402E',
  sci: '#2F6B4E',
  tech: '#3B7A9B',
  exploration: '#C95F1F',
  astro: '#5661B3',
  religion: '#9C4A6E',
};
const CAT_FALLBACK = '#8E8B81';

export function catColor(cat: string | undefined): string {
  return (cat && CAT_COLORS[cat]) || CAT_FALLBACK;
}

/** Track cell shared by the timeline tables — verbose label + colored
 *  chip keyed off the cat short code. */
export function TrackChip({ track, cat }: { track: string; cat?: string }) {
  const color = catColor(cat);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        title={cat ?? ''}
        style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 999, background: color, flexShrink: 0 }}
      />
      <span style={{ fontSize: 12 }}>{track}</span>
    </span>
  );
}

/** Source cell shared by the timeline tables — clickable when a
 *  sourceUrl is present, plain sourceLabel text otherwise. */
export function SourceCell({ sourceLabel, sourceUrl }: { sourceLabel?: string; sourceUrl?: string }) {
  if (!sourceLabel && !sourceUrl) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>;
  if (!sourceUrl) return <span style={{ fontSize: 12 }}>{sourceLabel}</span>;
  return (
    <a
      href={sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={sourceUrl}
      style={{ color: 'var(--accent-primary)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{sourceLabel ?? sourceUrl}</span>
      <ExternalLink size={11} aria-hidden="true" style={{ flexShrink: 0 }} />
    </a>
  );
}

// ── Component ───────────────────────────────────────────────────────

// Sort accessors (CTSK-267) — module-level so the useTableSort memo
// key is referentially stable. Date/Span sort numerically by year
// (negative = BCE, so BCE < CE); everything else is string compare.
const EVENT_SORT_ACCESSORS = {
  name:     (e: DeepTimeEvent) => e.name,
  date:     (e: DeepTimeEvent) => parseYear(e.date),
  endDate:  (e: DeepTimeEvent) => (e.endDate ? parseYear(e.endDate) : null),
  track:    (e: DeepTimeEvent) => e.track,
  location: (e: DeepTimeEvent) => e.location ?? null,
  desc:     (e: DeepTimeEvent) => e.desc ?? null,
  source:   (e: DeepTimeEvent) => e.sourceLabel ?? e.sourceUrl ?? null,
};

export function EventsTable({ yamlText }: { yamlText: string }) {
  const { entries, parseError } = useMemo<{
    entries: DeepTimeEvent[];
    parseError: string | null;
  }>(() => {
    if (!yamlText) return { entries: [], parseError: null };
    try {
      const parsed = yaml.load(yamlText) as EventsFile | null;
      const list = ((parsed?.events ?? []) as DeepTimeEvent[]).map(normalizeEvent);
      return { entries: list, parseError: null };
    } catch (err) {
      return { entries: [], parseError: (err as Error).message };
    }
  }, [yamlText]);

  // ── CTSK-267: search / filter / sort ──────────────────────────────
  const [search, setSearch] = useState('');
  const [filterTrack, setFilterTrack] = useState('');
  const [filterCat, setFilterCat] = useState('');
  // BCE-aware year range — plain number inputs (negative = BCE)
  // because native date inputs can't express negative years.
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');

  // Open-ended vocab → dropdown options computed from the loaded data.
  const trackOptions = useMemo(
    () => [...new Set(entries.map((e) => e.track).filter(Boolean))].sort(),
    [entries],
  );
  const catOptions = useMemo(
    () => [...new Set(entries.map((e) => e.cat).filter((c): c is string => !!c))].sort(),
    [entries],
  );

  const filtered = useMemo(() => {
    let rows = entries;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((e) => {
        const hay = [e.name, coerceDateString(e.date), e.track, e.cat ?? '', e.location ?? '', e.desc ?? '', e.sourceLabel ?? '']
          .join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    if (filterTrack) rows = rows.filter((e) => e.track === filterTrack);
    if (filterCat) rows = rows.filter((e) => e.cat === filterCat);
    const yFrom = yearFrom.trim() === '' ? null : Number(yearFrom);
    const yTo = yearTo.trim() === '' ? null : Number(yearTo);
    if (yFrom != null || yTo != null) {
      rows = rows.filter((e) => {
        const y = parseYear(e.date);
        if (y == null) return false;
        if (yFrom != null && !Number.isNaN(yFrom) && y < yFrom) return false;
        if (yTo != null && !Number.isNaN(yTo) && y > yTo) return false;
        return true;
      });
    }
    return rows;
  }, [entries, search, filterTrack, filterCat, yearFrom, yearTo]);

  // Tri-state sortable columns (SortableTh cycles asc → desc → none;
  // none = the file's natural order). Default stays chronological asc
  // — the pre-CTSK-267 behavior of this table.
  const { sorted, sort, setSort } = useTableSort(filtered, EVENT_SORT_ACCESSORS, { key: 'date', dir: 'asc' });

  const reset = () => {
    setSearch('');
    setFilterTrack('');
    setFilterCat('');
    setYearFrom('');
    setYearTo('');
    setSort(null);
  };

  return (
    <div>
      {parseError && (
        <div role="alert" style={{ padding: 12, background: 'color-mix(in srgb, var(--danger-primary, #B3261E) 12%, var(--surface-raised))', border: '1px solid var(--danger-primary, #B3261E)', borderRadius: 'var(--radius-md)', color: 'var(--danger-primary, #B3261E)', fontSize: 13, marginBottom: 16 }}>
          <strong>YAML parse error:</strong> {parseError}
        </div>
      )}

      {entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
          No events yet — add entries under <code style={{ fontFamily: 'var(--font-mono)' }}>events:</code> in{' '}
          <code style={{ fontFamily: 'var(--font-mono)' }}>captain-tasko/registry/events.yaml</code>.
        </div>
      ) : (
        <>
          <RegistryTableHeader
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search name, track, location, description…"
            filters={[
              { label: 'All tracks', value: filterTrack, options: trackOptions, onChange: setFilterTrack },
              { label: 'All categories', value: filterCat, options: catOptions, onChange: setFilterCat },
            ]}
            onReset={reset}
          >
            <input
              type="number"
              value={yearFrom}
              onChange={(e) => setYearFrom(e.target.value)}
              placeholder="Year from"
              aria-label="Year from"
              title="Earliest year (negative for BCE, e.g. -2686)"
              style={yearInputStyle}
            />
            <input
              type="number"
              value={yearTo}
              onChange={(e) => setYearTo(e.target.value)}
              placeholder="Year to"
              aria-label="Year to"
              title="Latest year (negative for BCE)"
              style={yearInputStyle}
            />
          </RegistryTableHeader>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            {sorted.length === entries.length
              ? `${entries.length} events`
              : `Showing ${sorted.length} of ${entries.length} events`}
          </div>
          {sorted.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
              No events match.
            </div>
          ) : (
          <table className="grid" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 230 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 130 }} />
              <col />
              <col style={{ width: 190 }} />
            </colgroup>
            <thead>
              <tr>
                <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Name</SortableTh>
                <SortableTh sortKey="date" sort={sort} onSortChange={setSort}>Date</SortableTh>
                <SortableTh sortKey="endDate" sort={sort} onSortChange={setSort}>Span</SortableTh>
                <SortableTh sortKey="track" sort={sort} onSortChange={setSort}>Track</SortableTh>
                <SortableTh sortKey="location" sort={sort} onSortChange={setSort}>Location</SortableTh>
                <SortableTh sortKey="desc" sort={sort} onSortChange={setSort}>Description</SortableTh>
                <SortableTh sortKey="source" sort={sort} onSortChange={setSort}>Source</SortableTh>
              </tr>
            </thead>
            <tbody>
              {sorted.map((e, idx) => {
                // CTSK-264 — read-time validation (same dispatcher as
                // the CTSK-255 write path, on the normalized entry).
                const validation = validateRegistryEntry('events-v1', e);
                const cells = (
                  <>
                  <td style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.name}>
                    {e.name}
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }} title={coerceDateString(e.date)}>
                    {formatBceDate(e.date)}
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.endDate ? `${coerceDateString(e.date)} – ${coerceDateString(e.endDate)}` : ''}>
                    {formatBceSpan(e.date, e.endDate)}
                  </td>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.track}>
                    <TrackChip track={e.track} cat={e.cat} />
                  </td>
                  <td style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.location ?? ''}>
                    {e.location ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.desc ?? ''}>
                    {e.desc ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <SourceCell sourceLabel={e.sourceLabel} sourceUrl={e.sourceUrl} />
                  </td>
                  </>
                );
                const key = `${e.name}-${idx}`;
                return validation.ok
                  ? <tr key={key}>{cells}</tr>
                  : <WarningRow key={key} issues={validation.errors}>{cells}</WarningRow>;
              })}
            </tbody>
          </table>
          )}
        </>
      )}
    </div>
  );
}

const yearInputStyle: React.CSSProperties = {
  width: 110,
  padding: '6px 10px',
  fontSize: 13,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--surface-raised)',
};

// ── Helpers ─────────────────────────────────────────────────────────

// BCE dates may come out of js-yaml as strings (typical) or, for CE
// dates, as JS Date objects per the YAML 1.1 timestamp tag. Coerce
// date-ish fields to strings up-front so no cell ever renders a raw
// Date (JSX throws) or an "Invalid Date" fallback. Folded desc blocks
// carry a trailing newline — trim.
function normalizeEvent(e: DeepTimeEvent): DeepTimeEvent {
  const out = { ...e };
  out.date = coerceDateString(out.date as unknown);
  if (out.endDate != null) out.endDate = coerceDateString(out.endDate as unknown);
  if (typeof out.desc === 'string') out.desc = out.desc.trim();
  return out;
}
